import { useUserRole } from "@/hooks/useUserRole";
import { useAgentMode } from "@/hooks/useAgentMode";
import { useUserOrg } from "@/hooks/useUserOrg";
import { useOrgProfileStrength } from "@/hooks/useOrgProfileStrength";
import { decideOrgOnboarding } from "@/lib/orgOnboardingDecision";

/**
 * Whether to nudge this admin about their organization profile.
 *
 * Previously read profile_data.onboardingCompleted, a flag the wizard set after
 * six steps that silenced every nudge permanently at roughly a fifth of a real
 * profile. It now reads profile strength, with the softeners in
 * orgOnboardingDecision — a dismissal snoozes only the auto-open, and an org
 * that completed the old wizard is never auto-opened again.
 *
 * Mutual exclusivity with the agent wizard is unchanged and still enforced by
 * mode: eligible only for an admin in admin context (isAgentMode === false).
 */
export const useOrgOnboardingStatus = () => {
  const { isAdmin, isLoading: roleLoading } = useUserRole();
  const { isAgentMode } = useAgentMode();
  const { org, isLoading: orgLoading, refetch } = useUserOrg();
  const { strength, isLoading: strengthLoading } = useOrgProfileStrength();

  const settled = !roleLoading && !orgLoading && !strengthLoading;
  const eligible = settled && isAdmin && !isAgentMode && !!org && !!strength;

  const pd = ((org as unknown as { profile_data?: Record<string, unknown> } | null)
    ?.profile_data ?? {}) as Record<string, unknown>;

  const decision = eligible
    ? decideOrgOnboarding({
        tier: strength!.tier,
        legacyCompleted: !!pd.onboardingCompleted,
        dismissedAt: (pd.onboardingDismissedAt as string | undefined) ?? null,
        now: new Date(),
      })
    : { showBanner: false, allowAutoOpen: false };

  return {
    settled,
    org,
    showBanner: decision.showBanner,
    allowAutoOpen: decision.allowAutoOpen,
    refetchOrg: refetch,
  };
};
